import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateInput from "./DateInput";
import { modSelect, noShiftTextSelect } from "../lib/mouseSelect";
import { fetchThumb } from "../lib/thumbCache";
import { pdfFirstPageImage } from "../lib/pdfThumb";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { CollapsibleSection, ViewToggle } from "./CollapsibleSection";
import { HoverPreview, PreviewToggle, usePreviewPref } from "./HoverPreview";
import { Briefcase, FileText, Loader2, Trash2, Save, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ProMessaggi } from "./ProMessaggi";

const RUOLI = [["avvocato", "Legale", "avvocati"], ["medico_legale", "Medico", "medici_legali"], ["mandante", "Mandante", "mandanti"], ["perito", "Perito", "periti"], ["assistito", "Assistito", null], ["cessionario", "Cessionario", "cessionari"]];
export const WEB_TAB_LABEL = Object.fromEntries(RUOLI.map(([k, l]) => [k, l]));
const EDITOR_RUOLI = ["medico_legale", "avvocato"];
const LBL = "text-[11px] uppercase tracking-wider text-slate-400 font-mono";
const anagName = (r) => r.denominazione || r.ragione_sociale || [r.cognome, r.nome].filter(Boolean).join(" ");
const appUrl = () => window.location.origin;

const Picker = ({ atype, onPick }) => {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get(`/anagrafiche/${atype}`).then(({ data }) => setList(Array.isArray(data) ? data : data.items || [])).catch(() => {}); }, [atype]);
  const shown = list.filter((r) => anagName(r).toLowerCase().includes(q.toLowerCase())).slice(0, 30);
  return (
    <div className="space-y-2" data-testid={`pro-picker-${atype}`}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca in anagrafica…" data-testid={`pro-picker-search-${atype}`} className="w-full h-9 rounded-md bg-slate-900/60 border border-slate-700 px-3 text-sm text-slate-100" />
      <div className="max-h-48 overflow-y-auto rounded-md border border-slate-700 divide-y divide-slate-800">
        {shown.length === 0 && <p className="p-3 text-xs text-slate-500">Nessun professionista in anagrafica.</p>}
        {shown.map((r) => (
          <button key={r.id} type="button" onClick={() => onPick(r)} data-testid={`pro-pick-${r.id}`} className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-amber-500/10">{anagName(r)}</button>
        ))}
      </div>
    </div>
  );
};

const Thumb = ({ pid, a }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const ct = (a.content_type || "").toLowerCase();
    if (!(ct.startsWith("image/") || ct === "application/pdf")) { return; }
    let alive = true;
    fetchThumb(pid, a.id, ct).then(async (u) => {
      const v = u || (ct === "application/pdf"
        ? await pdfFirstPageImage(`s-${a.id}`, async () => (await api.get(`/studio/pratiche/${pid}/allegati-files/${a.id}/content`, { responseType: "blob" })).data)
        : null);
      if (alive && v) { setUrl(v); }
    }).catch(() => {});
    return () => { alive = false; };
  }, [pid, a.id, a.content_type]);
  if (url) return <img src={url} alt={a.nome} draggable={false} className="h-full w-full object-cover pointer-events-none" loading="lazy" />;
  return <FileText className="h-8 w-8 text-slate-500" />;
};

const AllegatiCheck = ({ pid, allegati, value, onChange, view, preview }) => {
  const lastRef = useRef(null);
  const tog = (id, v) => { lastRef.current = id; onChange(v ? [...value, id] : value.filter((x) => x !== id)); };
  const order = allegati.map((a) => a.id);
  const modClick = (id) => (e) => modSelect(e, id, order, lastRef,
    (x) => onChange(value.includes(x) ? value.filter((y) => y !== x) : [...value, x]),
    (ids) => onChange([...new Set([...value, ...ids])]));
  const thumbUrl = (a) => {
    const ct = (a.content_type || "").toLowerCase();
    if (!(ct.startsWith("image/") || ct === "application/pdf")) return Promise.resolve(null);
    return api.get(`/studio/pratiche/${pid}/allegati-files/${a.id}/thumb`).then(({ data }) => data?.url || (ct === "application/pdf"
      ? pdfFirstPageImage(`s-${a.id}`, async () => (await api.get(`/studio/pratiche/${pid}/allegati-files/${a.id}/content`, { responseType: "blob" })).data)
      : null));
  };
  const allSel = allegati.length > 0 && allegati.every((a) => value.includes(a.id));
  const bar = allegati.length > 0 && (
    <div className="flex items-center gap-3 flex-wrap mb-2" data-testid="pro-allegati-selbar">
      <button type="button" onClick={() => onChange(allSel ? [] : order)} data-testid="pro-allegati-select-all" className="h-8 px-3 rounded-md border border-slate-600 text-xs font-semibold text-slate-200 hover:bg-slate-800 inline-flex items-center gap-2">
        <Checkbox checked={allSel} className="pointer-events-none" tabIndex={-1} /> {allSel ? "Deseleziona tutto" : "Seleziona tutto"}
      </button>
      <span className="text-xs text-slate-300" data-testid="pro-allegati-selcount">{value.filter((id) => order.includes(id)).length} di {allegati.length} selezionati</span>
      <span className="hidden sm:inline text-[11px] text-slate-500">Ctrl+clic (Cmd su Mac) per scegliere più file, Maiusc+clic per un intervallo.</span>
    </div>
  );
  if (view === "grid") return (
    <div>{bar}
    <div className="max-h-96 overflow-y-auto grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 p-1" data-testid="pro-allegati-grid">
      {allegati.length === 0 && <p className="p-3 text-xs text-slate-500 col-span-full">Nessun allegato nella pratica.</p>}
      {allegati.map((a) => (
        <HoverPreview key={a.id} enabled={preview} loadUrl={() => thumbUrl(a)} cacheKey={`st-${a.id}`} title={a.nome}>
        <label onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect} className={`relative block rounded-md border overflow-hidden cursor-pointer select-none ${value.includes(a.id) ? "border-amber-500 ring-1 ring-amber-500" : "border-slate-700"}`}>
          <div className="aspect-[4/3] bg-slate-900/40 grid place-items-center overflow-hidden"><Thumb pid={pid} a={a} /></div>
          <div className="absolute top-1.5 left-1.5 rounded bg-white/90 p-0.5"><Checkbox checked={value.includes(a.id)} onCheckedChange={(v) => tog(a.id, v)} data-testid={`pro-allegato-${a.id}`} /></div>
          <div className="px-2 py-1 text-[11px] text-slate-200 truncate">{a.nome}</div>
          {a.caricato_da_pro && <div className="px-2 pb-1 text-[10px] text-violet-300 truncate">caricato da {a.caricato_da_pro.nome}</div>}
        </label>
        </HoverPreview>
      ))}
    </div>
    </div>
  );
  return (
    <div>{bar}
    <div className="max-h-52 overflow-y-auto rounded-md border border-slate-700 divide-y divide-slate-800" data-testid="pro-allegati-check">
      {allegati.length === 0 && <p className="p-3 text-xs text-slate-500">Nessun allegato nella pratica.</p>}
      {allegati.map((a) => (
        <HoverPreview key={a.id} enabled={preview} loadUrl={() => thumbUrl(a)} cacheKey={`st-${a.id}`} title={a.nome}>
        <label onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-200 cursor-pointer hover:bg-slate-800/40 select-none">
          <Checkbox checked={value.includes(a.id)} onCheckedChange={(v) => tog(a.id, v)} data-testid={`pro-allegato-${a.id}`} />
          <span className="truncate">{a.nome}</span>
          {a.caricato_da_pro && <span className="ml-auto shrink-0 text-[10px] text-violet-300">caricato da {a.caricato_da_pro.nome}</span>}
        </label>
        </HoverPreview>
      ))}
    </div>
    </div>
  );
};

const useAutoSave = (value, init, save) => {
  const [saved, setSaved] = useState(init);
  const [err, setErr] = useState(false);
  const same = (x, y) => JSON.stringify(x) === JSON.stringify(y);
  const pending = useRef(null);
  pending.current = same(value, saved) ? null : value;
  useEffect(() => {
    if (same(value, saved)) { return undefined; }
    setErr(false);
    const t = setTimeout(() => { save(value).then(() => setSaved(value)).catch(() => setErr(true)); }, 800);
    return () => clearTimeout(t);
  }, [value, saved, save]);
  useEffect(() => () => { if (pending.current !== null) { save(pending.current).catch(() => {}); } }, [save]);
  return same(value, saved) ? "ok" : err ? "err" : "saving";
};
const SAVE_LBL = { ok: "Salvato", saving: "Salvataggio…", err: "Errore di salvataggio: riprova" };

const ConsegnaStudio = ({ a, base, onChanged }) => {
  const patch = async (body, msg) => {
    try { await api.patch(base, body); if (msg) toast.success(msg); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-md border border-slate-700 p-3 space-y-2" data-testid={`pro-consegna-${a.ruolo}`}>
      <span className={LBL}>Data di consegna relazione / atto</span>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-44"><DateInput value={a.scadenza_consegna || ""} onChange={(v) => patch({ scadenza_consegna: v || "" }, v ? "Data di consegna salvata" : "Data di consegna rimossa")} testId={`pro-scadenza-${a.ruolo}`} className="bg-slate-900/60 border-slate-700" /></div>
        {a.scadenza_consegna && (a.consegnato_at
          ? <span className="text-xs text-emerald-300 inline-flex items-center gap-2" data-testid={`pro-consegnato-${a.ruolo}`}>Consegnato il {new Date(a.consegnato_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
              <button type="button" onClick={() => patch({ consegnato: false })} data-testid={`pro-consegnato-annulla-${a.ruolo}`} className="text-slate-400 hover:text-slate-200 underline">annulla</button></span>
          : <button type="button" onClick={() => patch({ consegnato: true }, "Segnato come consegnato")} data-testid={`pro-segna-consegnato-${a.ruolo}`} className="text-xs text-emerald-300 hover:underline">Segna come consegnato</button>)}
      </div>
      <p className="text-[11px] text-slate-500">Promemoria automatico via email al professionista 3 giorni prima e il giorno della scadenza. Diventa «consegnato» quando salva una relazione/atto dall'editor del portale.</p>
      <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <Checkbox checked={a.avviso_studio_scadenza} onCheckedChange={(v) => patch({ avviso_studio_scadenza: !!v })} data-testid={`pro-avviso-studio-${a.ruolo}`} /> Avvisa lo studio (app ed email) se la scadenza passa senza consegna
      </label>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer pt-1 border-t border-slate-800">
        <Checkbox checked={a.mostra_dinamica} onCheckedChange={(v) => patch({ mostra_dinamica: !!v }, v ? "Dinamica del sinistro visibile al professionista" : "Dinamica del sinistro nascosta al professionista")} data-testid={`pro-mostra-dinamica-${a.ruolo}`} /> Mostra al professionista la dinamica del sinistro (sola lettura)
      </label>
    </div>
  );
};

const EV_LBL = { professionista: "Professionista", studio: "Studio" };
const Cronologia = ({ base, a, ver }) => {
  const [ev, setEv] = useState(null);
  useEffect(() => { api.get(`${base}/cronologia`).then(({ data }) => setEv(data)).catch(() => setEv([])); }, [base, a, ver]);
  return (
    <CollapsibleSection title={`Cronologia incarico${ev ? ` (${ev.length})` : ""}`} testId={`pro-sec-crono-${a.ruolo}`}>
      {!ev ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : ev.length === 0 ? <p className="text-xs text-slate-500">Nessun movimento.</p> : (
        <ol className="max-h-72 overflow-y-auto rounded-md border border-slate-700 divide-y divide-slate-800" data-testid={`pro-crono-${a.ruolo}`}>
          {ev.map((e, i) => (
            <li key={`${e.at}-${i}`} className="flex items-start gap-3 px-3 py-2 text-sm" data-testid={`pro-crono-item-${a.ruolo}`}>
              <span className="shrink-0 w-[7.5rem] text-[11px] font-mono text-slate-400 pt-0.5">{e.at ? new Date(e.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${e.da === "professionista" ? "bg-violet-500/20 text-violet-300" : "bg-amber-500/20 text-amber-300"}`}>{EV_LBL[e.da] || "Studio"}</span>
              <span className="min-w-0 text-slate-200 break-words">{e.testo}</span>
            </li>
          ))}
        </ol>
      )}
    </CollapsibleSection>
  );
};

const fmtAcc = (s) => new Date(s).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const Accessi = ({ base, a }) => {
  const [acc, setAcc] = useState(null);
  useEffect(() => { api.get(`${base}/accessi`).then(({ data }) => setAcc(data)).catch(() => setAcc([])); }, [base, a]);
  const ultimo = acc && acc[0];
  const aperture = acc ? acc.filter((x) => x.tipo === "apertura") : [];
  return (
    <CollapsibleSection title={`Accessi del professionista${acc ? ` (${acc.length})` : ""}`} testId={`pro-sec-accessi-${a.ruolo}`}>
      {!acc ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : acc.length === 0 ? <p className="text-xs text-slate-500" data-testid={`pro-accessi-vuoto-${a.ruolo}`}>Il professionista non ha ancora effettuato accessi al portale.</p> : (
        <div className="space-y-2">
          <p className="text-xs text-slate-300" data-testid={`pro-accessi-ultimo-${a.ruolo}`}>Ultimo accesso: <strong>{fmtAcc(ultimo.at)}</strong>{aperture.length > 0 && <> · ultima apertura di questa pratica: <strong>{fmtAcc(aperture[0].at)}</strong></>}</p>
          <ol className="max-h-60 overflow-y-auto rounded-md border border-slate-700 divide-y divide-slate-800" data-testid={`pro-accessi-${a.ruolo}`}>
            {acc.map((x, i) => (
              <li key={`${x.at}-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-3 px-3 py-1.5 text-sm" data-testid={`pro-accesso-item-${a.ruolo}`}>
                <span className="shrink-0 sm:w-[10.5rem] text-[11px] font-mono text-slate-400">{fmtAcc(x.at)}</span>
                <span className={x.tipo === "login" ? "text-slate-200" : "text-emerald-300"}>{x.tipo === "login" ? "Accesso al portale" : "Ha aperto questa pratica"}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </CollapsibleSection>
  );
};

const Incarico = ({ pid, a, allegati, onChanged, onRead, onSaved }) => {
  const [note, setNote] = useState(a.note);
  const [vis, setVis] = useState(a.allegati_visibili);
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("pgp_web_allegati_view") || "list");
  const setV = (v) => { setView(v); localStorage.setItem("pgp_web_allegati_view", v); };
  const [preview, setPreview] = usePreviewPref("pgp_web_hover_preview");
  const base = `/studio/pratiche/${pid}/professionisti/${a.id}`;
  const bozza = !a.avviato;
  const saveField = useCallback((field) => (val) => api.patch(base, { [field]: val }).then(({ data }) => onSaved(a.id, { [field]: data[field] }))
    .catch((e) => { toast.error("Non salvato: " + formatApiError(e.response?.data?.detail)); throw e; }), [base, a.id, onSaved]);
  const saveNote = useMemo(() => saveField("note"), [saveField]);
  const saveVis = useMemo(() => saveField("allegati_visibili"), [saveField]);
  const statoNote = useAutoSave(note, a.note, saveNote);
  const statoVis = useAutoSave(vis, a.allegati_visibili, saveVis);
  const mancanti = [!a.email && "email in anagrafica", !a.ha_accesso && "accesso portale in anagrafica", vis.length === 0 && !note.trim() && "almeno un allegato visibile o una nota"].filter(Boolean);
  useEffect(() => {
    if (bozza) { return; }
    api.get(`${base}/messaggi`).then(({ data }) => { setMsgs(data); if (a.non_letti_studio > 0) onRead(); }).catch(() => {});
  }, [base, bozza]); // eslint-disable-line react-hooks/exhaustive-deps
  const avvia = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/avvia`, { note, allegati_visibili: vis, app_url: appUrl() });
      toast.success(data.email_inviata ? `Incarico avviato: credenziali inviate a ${data.email}` : "Incarico avviato. Email non inviata: controlla email e accesso portale in anagrafica");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const salva = async () => {
    setBusy(true);
    try { await api.patch(base, { note, allegati_visibili: vis, ...(bozza ? {} : { app_url: appUrl() }) }); toast.success(bozza ? "Bozza salvata: nessuna email inviata" : "Salvato: il professionista è stato avvisato degli eventuali nuovi documenti"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setAttivo = async (v) => {
    try { await api.patch(base, { attivo: v }); toast.success(v ? "Sezione attivata: il professionista vede di nuovo la pratica" : "Sezione disattivata: il professionista non vede più la pratica"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const riapri = async () => {
    setBusy(true);
    try { await api.post(`${base}/riapri`, { app_url: appUrl() }); toast.success("Sezione riaperta: il professionista può di nuovo lavorare"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const rimuovi = async () => {
    if (!window.confirm(bozza ? `Annullare la preparazione dell'incarico per ${a.nome}?` : `Rimuovere ${a.nome} dall'incarico? Non vedrà più questa pratica.`)) return;
    await api.delete(base); onChanged();
  };
  const [cronoVer, setCronoVer] = useState(0);
  const send = async (testo) => {
    try { const { data } = await api.post(`${base}/messaggi`, { testo, app_url: appUrl() }); setMsgs((m) => [...m, data]); setCronoVer((v) => v + 1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); throw e; }
  };
  return (
    <div className="space-y-4" data-testid={`pro-incarico-${a.ruolo}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0"><div className="text-slate-100 font-semibold break-words">{a.nome}</div><div className="text-xs text-slate-400">{a.email || "email mancante"}{!a.ha_accesso && " · accesso portale non impostato in anagrafica"}</div></div>
        <div className="flex items-center gap-3 flex-wrap">
          {!bozza && <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <Switch checked={a.attivo} onCheckedChange={setAttivo} data-testid={`pro-attivo-${a.ruolo}`} /> {a.attivo ? "Sezione attiva" : "Sezione disattivata"}
          </label>}
          <button type="button" onClick={rimuovi} data-testid={`pro-remove-${a.ruolo}`} className="text-red-400 hover:text-red-300 text-xs inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> {bozza ? "Cambia / annulla" : "Rimuovi"}</button>
        </div>
      </div>
      {bozza && <p className="text-xs text-slate-950 font-medium rounded-md border border-amber-500 bg-amber-400 px-3 py-2" data-testid={`pro-bozza-${a.ruolo}`}>Incarico in preparazione: il professionista non vede ancora nulla e non ha ricevuto email. Configura note e allegati, poi premi «Avvia incarico».</p>}
      {!bozza && !a.attivo && <p className="text-xs text-amber-300 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">Sezione disattivata: il professionista non vede questa pratica nel portale.</p>}
      {a.concluso_at && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 space-y-2" data-testid={`pro-concluso-${a.ruolo}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm text-emerald-300 font-semibold">Conclusa dal professionista il {new Date(a.concluso_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
            <button type="button" onClick={riapri} disabled={busy} data-testid={`pro-riapri-${a.ruolo}`} className="h-8 px-3 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Riapri sezione</button>
          </div>
          {a.riapertura_richiesta && <p className="text-xs text-amber-200" data-testid={`pro-riapertura-motivo-${a.ruolo}`}>Richiesta di riapertura del {new Date(a.riapertura_richiesta.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}: «{a.riapertura_richiesta.motivo}»</p>}
        </div>
      )}
      <div><span className={LBL}>Note (visibili al professionista, modificabili solo dallo studio)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} data-testid={`pro-note-${a.ruolo}`} className="mt-1 w-full rounded-md bg-slate-900/60 border border-slate-700 px-3 py-2 text-sm text-slate-100" />
        <span className="text-[11px] text-slate-500" data-testid={`pro-note-stato-${a.ruolo}`}>{statoNote === "ok" ? "Note salvate" : SAVE_LBL[statoNote]}</span></div>
      {EDITOR_RUOLI.includes(a.ruolo) && <ConsegnaStudio a={a} base={base} onChanged={onChanged} />}
      <CollapsibleSection title={`Allegati visibili al professionista (${vis.filter((id) => allegati.some((x) => x.id === id)).length})`} testId={`pro-sec-allegati-${a.ruolo}`} actions={<div className="flex items-center gap-3 flex-wrap"><PreviewToggle on={preview} onChange={setPreview} testId={`pro-hover-preview-${a.ruolo}`} /><ViewToggle view={view} onChange={setV} testId={`pro-allegati-view-${a.ruolo}`} /></div>}>
        <AllegatiCheck pid={pid} allegati={allegati} value={vis} onChange={setVis} view={view} preview={preview} />
        <span className="text-[11px] text-slate-500" data-testid={`pro-vis-stato-${a.ruolo}`}>Selezione allegati: {SAVE_LBL[statoVis].toLowerCase()}</span>
      </CollapsibleSection>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={salva} disabled={busy} data-testid={`pro-save-${a.ruolo}`} className={`h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50 ${bozza ? "border border-slate-600 text-slate-200 hover:bg-slate-800" : "bg-amber-500 hover:bg-amber-600 text-slate-950"}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {bozza ? "Salva bozza" : "Salva e avvisa il professionista"}
        </button>
        {bozza && <button type="button" onClick={avvia} disabled={busy || mancanti.length > 0} data-testid={`pro-avvia-${a.ruolo}`} className="h-9 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-950 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Avvia incarico
        </button>}
      </div>
      {bozza && mancanti.length > 0 && <p className="text-xs text-slate-400" data-testid={`pro-avvia-mancanti-${a.ruolo}`}>Per avviare manca: {mancanti.join(", ")}.</p>}
      {!bozza && <CollapsibleSection title="Messaggi con il professionista" testId={`pro-sec-msg-${a.ruolo}`}><ProMessaggi messaggi={msgs} mine="studio" onSend={send} testId={`pro-msg-${a.ruolo}`} /></CollapsibleSection>}
      {!bozza && <Accessi base={base} a={a} />}
      {!bozza && <Cronologia base={base} a={a} ver={cronoVer} />}
    </div>
  );
};

const ModelliPortale = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/studio/portale/modelli").then(({ data }) => setList(data)).catch(() => setList([]));
  const toggle = async (id) => {
    const next = list.map((t) => (t.id === id ? { ...t, attivo: !t.attivo } : t));
    setList(next);
    await api.put("/studio/portale/modelli", { template_ids: next.filter((t) => t.attivo).map((t) => t.id) }).catch(() => toast.error("Salvataggio non riuscito"));
  };
  if (!list) return <button type="button" onClick={load} data-testid="pro-modelli-open" className="text-xs text-violet-300 hover:underline">Scegli i modelli lettera disponibili nel portale per Medico Legale e Avvocato</button>;
  return (
    <div className="rounded-md border border-violet-500/30 p-2 max-h-48 overflow-y-auto space-y-1" data-testid="pro-modelli-list">
      {list.map((t) => (
        <label key={t.id} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer"><Checkbox checked={t.attivo} onCheckedChange={() => toggle(t.id)} data-testid={`pro-modello-${t.id}`} /> {t.title}</label>
      ))}
    </div>
  );
};

const AvvioIncarico = ({ label, atype, ruolo, onScegli }) => {
  const [aperta, setAperta] = useState(false);
  if (!aperta) return (
    <div className="rounded-md border border-dashed border-slate-600 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`pro-sezione-off-${ruolo}`}>
      <p className="text-sm text-slate-400">Sezione {label} non attiva per questa pratica.</p>
      <button type="button" onClick={() => setAperta(true)} data-testid={`pro-attiva-${ruolo}`} className="h-9 px-4 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold">Attiva sezione</button>
    </div>
  );
  return (
    <div className="space-y-3" data-testid={`pro-avvio-${ruolo}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-300">Scegli il {label} dall'anagrafica: potrai poi configurare note e allegati prima di avviare l'incarico. Nessuna email parte ora.</p>
        <button type="button" onClick={() => setAperta(false)} data-testid={`pro-disattiva-${ruolo}`} className="text-xs text-slate-400 hover:text-slate-200 shrink-0">Chiudi sezione</button>
      </div>
      <Picker atype={atype} onPick={onScegli} />
    </div>
  );
};

export const ProfessionistiDialog = ({ open, onOpenChange, pratica }) => {
  const [assign, setAssign] = useState([]);
  const [allegati, setAllegati] = useState([]);
  const pid = pratica?.id;
  const onSaved = useCallback((id, patch) => setAssign((l) => l.map((x) => (x.id === id ? { ...x, ...patch } : x))), []);
  const load = useCallback(() => api.get(`/studio/pratiche/${pid}/professionisti`).then(({ data }) => setAssign(data)).catch(() => {}), [pid]);
  useEffect(() => {
    if (!open || !pid) { return; }
    load();
    api.get(`/studio/pratiche/${pid}/allegati-all`).then(({ data }) => setAllegati(Array.isArray(data) ? data : data.files || [])).catch(() => {});
  }, [open, pid, load]);
  const assegna = async (ruolo, r) => {
    try {
      await api.post(`/studio/pratiche/${pid}/professionisti`, { ruolo, anag_id: r.id, app_url: appUrl() });
      toast.success(`${anagName(r)} selezionato: configura l'incarico e premi «Avvia incarico»`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="professionisti-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Briefcase className="h-4 w-4 text-violet-400" /> Web — {pratica?.numero_pratica}</DialogTitle></DialogHeader>
        <Tabs defaultValue="avvocato" className="min-w-0">
          <TabsList className="flex flex-wrap h-auto">
            {RUOLI.map(([k, l]) => {
              const a = assign.find((x) => x.ruolo === k);
              return <TabsTrigger key={k} value={k} data-testid={`pro-tab-${k}`}>{l}{a?.non_letti_studio > 0 && <span className="ml-1.5 rounded-full bg-red-500 text-white text-[10px] px-1.5">{a.non_letti_studio}</span>}</TabsTrigger>;
            })}
          </TabsList>
          {RUOLI.map(([k, l, atype]) => {
            const a = assign.find((x) => x.ruolo === k);
            return (
              <TabsContent key={k} value={k} className="pt-3 space-y-4">
                {k === "assistito" ? <p className="text-sm text-slate-400" data-testid="web-tab-assistito">L'assistito accede con la sua Area Cliente (credenziali dalla scheda cliente): qui non serve assegnare nessuno.</p> : a ? <Incarico key={a.id + a.anag_id} pid={pid} a={a} allegati={allegati} onChanged={load} onRead={load} onSaved={onSaved} /> : (
                  <AvvioIncarico label={l} atype={atype} ruolo={k} onScegli={(r) => assegna(k, r)} />
                )}
                {(k === "medico_legale" || k === "avvocato") && <ModelliPortale />}
              </TabsContent>
            );
          })}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
